import * as Accordion from "#root/components/accordion";
import { GlyphLoading } from "#root/components/glyphs";
import { WithSidebar } from "#root/components/withSidebar";
import { getMonthlyGlobalUsageCollection } from "#root/schema/usage";
import { useData } from "#root/utility/hooks";
import { getMonthList, type MonthListEntry, monthNames } from "#root/utility/months";
import formatNumber from "cloudquote-number-format";
import type { ReactNode } from "react";
import { Fragment, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import PageLayout from "./layout";

export default function UsagePage({ ymd }: { ymd: string }): ReactNode {
  const months = useMemo(() => getMonthList(), []);

  return (
    <PageLayout title="Usage">
      <WithSidebar>
        <UsageSidebar months={months} ymd={ymd}/>
        <section>
          {ymd ? (
            <UsageTable ymd={ymd}/>
          ) : (
            <Fragment>
              <h2>API Usage</h2>
              <p>Please select a month to view your API Usage</p>
            </Fragment>
          )}
        </section>
      </WithSidebar>
    </PageLayout>
  );
}

function UsageSidebar({ ymd, months }: { ymd: string; months: MonthListEntry[] }) {
  return (
    <Accordion.Parent initialItemId={ymd} initialGroupId="By Month">
      <Accordion.Group label="By Month">
        {months.map(month => (
          <Accordion.Item key={month.ymd} itemId={month.ymd}>
            <Link to={`/billing/usage/${month.ymd}`}>{month.name}</Link>
          </Accordion.Item>
        ))}
      </Accordion.Group>
    </Accordion.Parent>
  );
}

function UsageTable({ ymd }: { ymd: string }) {
  const [details] = useData(async () => {
    if (!ymd) {
      return null;
    }

    return getMonthlyGlobalUsageCollection(ymd).list();
  }, [ymd]);

  const field = useCallback(
    (row: Array<number | string>, name: string) => {
      if (!details) {
        return null;
      }

      const idx = details.columns?.indexOf(name);
      return idx == null ? null : row[idx];
    },
    [details],
  );

  const date = new Date(ymd);

  return (
    <div className="usage-table">
      <h1>
        {monthNames[date.getUTCMonth()]} {date.getUTCFullYear()} - API Requests
      </h1>
      {details ? (
        <table className="table table-striped">
          <thead>
          <tr>
            <th>Account</th>
            <th>Deployment</th>
            <th>Name</th>
            <th>Unit</th>
            <th>Count</th>
          </tr>
          </thead>
          <tbody>
          {details.rows.map(row => (
            <tr key={field(row, "id")}>
              <td>{field(row, "accountName")}</td>
              <td>{field(row, "deploymentName")}</td>
              <td>{field(row, "name")}</td>
              <td>{field(row, "unit")}</td>
              <td>{formatNumber(field(row, "requests") as number, { Comma: true })}</td>
            </tr>
          ))}
          </tbody>
        </table>
      ) : (
        <GlyphLoading/>
      )}
    </div>
  );
}
